import { type Observable, of } from 'rxjs';
import type { HttpContext } from '@angular/common/http';
import type { DefaultSort } from './default-sort';
import type { BasePaginationDTO, ListPaginatedDTO } from '@pct-sdk/core';
import { forkJoinWithMapper } from '@pct/rxjs';
import type { GetAllRequestParameters } from './get-all-request-parameters';
import type { Constructor } from '@pct-ts/typing';
import { inject, Injector } from '@angular/core';

export abstract class AbstractDtoService<
  SERVICE,
  CREATE_UPDATE_DTO,
  GET_DTO,
  LIST_DTO,
  ID_KEY extends keyof GET_DTO,
  GET_ALL_PARAMETERS = any,
  PAGINATED_DTO extends BasePaginationDTO = any,
  ID_TYPE = GET_DTO[ID_KEY],
> {
  readonly #injector = inject(Injector);
  readonly #defaultSort: DefaultSort<LIST_DTO>;
  get defaultSort(): DefaultSort<LIST_DTO> {
    return this.#defaultSort;
  }
  protected readonly service: SERVICE;

  protected constructor(defaultSort: DefaultSort<LIST_DTO>, serviceType: Constructor<SERVICE>) {
    this.#defaultSort = defaultSort;
    this.service = this.injectService(serviceType);
  }

  abstract create(dto: CREATE_UPDATE_DTO, httpContext?: HttpContext): Observable<GET_DTO>;

  abstract update(
    id: ID_TYPE,
    dto: CREATE_UPDATE_DTO,
    httpContext?: HttpContext,
  ): Observable<GET_DTO | void>;

  abstract delete(id: ID_TYPE, httpContext?: HttpContext): Observable<void>;

  abstract getById(id: ID_TYPE, httpContext?: HttpContext): Observable<GET_DTO>;

  getByIds(ids: ID_TYPE[], httpContext?: HttpContext): Observable<GET_DTO[]> {
    if (ids.length === 0) {
      return of([]);
    }
    return forkJoinWithMapper(ids, id => this.getById(id, httpContext));
  }

  abstract getAll(
    parameters: GetAllRequestParameters<GET_ALL_PARAMETERS>,
    httpContext?: HttpContext,
  ): Observable<ListPaginatedDTO<LIST_DTO, PAGINATED_DTO>>;

  abstract getDtoId(dto: LIST_DTO): ID_TYPE;
  abstract getDtoId(dto: GET_DTO): ID_TYPE;
  abstract getDtoId(dto: GET_DTO | null | undefined): ID_TYPE | null | undefined;
  abstract getDtoId(dto: Partial<GET_DTO> | null | undefined): ID_TYPE | null | undefined;
  abstract getDtoId(dto: GET_DTO | Partial<GET_DTO> | undefined | null): ID_TYPE | undefined | null;

  abstract toString(dto: GET_DTO | LIST_DTO): string;

  protected injectService<S>(serviceType: Constructor<S>): S {
    return Injector.create({
      providers: [serviceType],
      parent: this.#injector,
    }).get(serviceType);
  }
}
